/**
 * What every volumetric map style agrees on (D169 the hologram, D210 the
 * scene): how big a room's footprint is for its kind, which way a corridor
 * turns, what a floor is called, and what a legend has to explain.
 *
 * @remarks
 * Extracted from the hologram's layout the day the scene arrived, so the
 * two drawings of one graph cannot disagree about the shape of a room. The
 * footprints are in CELLS — the grid the graph places rooms on — and say
 * nothing about height, which is each projection's own unit (pixels on the
 * hologram's screen, world units in the scene).
 *
 * @packageDocumentation
 */

import type { MapRoomKind } from '@orpheus/content-schema';

import type { MapGraph, MapGraphNode } from './map-graph.js';

/** A box's half-extents along and across its links, in cells. */
export interface Footprint {
	readonly across: number;
	readonly along: number;
}

/** What each kind of room stands on: a corridor long and narrow along its links, a chamber wide, a shaft thin. */
export const FOOTPRINTS: Readonly<Record<MapRoomKind, Footprint>> = {
	chamber: { across: 0.46, along: 0.46 },
	corridor: { across: 0.16, along: 0.48 },
	room: { across: 0.36, along: 0.36 },
	shaft: { across: 0.18, along: 0.18 },
};

/** Which axis a corridor runs along: the axis of its links, when they agree; otherwise it is drawn as a room. */
export const alongAxisOf = (graph: MapGraph, node: MapGraphNode): 'x' | 'y' | undefined => {
	const touching = graph.edges.filter(
		(edge) => edge.level === node.level && ((edge.fromX === node.x && edge.fromY === node.y) || (edge.toX === node.x && edge.toY === node.y)),
	);

	if (touching.length === 0) {
		return undefined;
	}

	if (touching.every((edge) => edge.fromY === edge.toY)) {
		return 'x';
	}

	return touching.every((edge) => edge.fromX === edge.toX) ? 'y' : undefined;
};

/**
 * The half-extents of a node's footprint in grid units — a corridor turned
 * along its links, and drawn as a room when its links run no one way.
 *
 * @param graph - The graph the node sits in
 * @param node - The node
 * @returns Half the footprint along x and along y, in cells
 */
export const footprintOf = (graph: MapGraph, node: MapGraphNode): { readonly halfX: number; readonly halfY: number } => {
	const footprint = FOOTPRINTS[node.kind];

	if (node.kind !== 'corridor') {
		return { halfX: footprint.across, halfY: footprint.across };
	}

	const axis = alongAxisOf(graph, node);

	if (axis === undefined) {
		return { halfX: FOOTPRINTS.room.across, halfY: FOOTPRINTS.room.across };
	}

	return axis === 'x' ? { halfX: footprint.along, halfY: footprint.across } : { halfX: footprint.across, halfY: footprint.along };
};

/** What a floor is called on the drawing. */
export const floorLabelOf = (level: number): string => `LEVEL ${String(level)}`;

/** What a legend may have to explain, given what a drawing contains. */
export type MapLegendKey = 'active' | 'checkpoint' | 'lift' | 'revealed' | 'unknown' | 'visited';

/**
 * What the drawing needs explained, in the legend's fixed order.
 *
 * @param graph - The drawn graph
 * @returns The keys, active first, lift last
 */
export const legendOf = (graph: MapGraph): readonly MapLegendKey[] => {
	const keys: MapLegendKey[] = ['active'];

	if (graph.nodes.some((node) => node.knowledge === 'visited' && !node.isActive)) {
		keys.push('visited');
	}

	if (graph.nodes.some((node) => node.knowledge === 'revealed')) {
		keys.push('revealed');
	}

	if (graph.nodes.some((node) => node.knowledge === 'unknown')) {
		keys.push('unknown');
	}

	if (graph.nodes.some((node) => node.isCheckpoint)) {
		keys.push('checkpoint');
	}

	if (graph.lifts.length > 0) {
		keys.push('lift');
	}

	return keys;
};
